<template>
  <div class="pwd-block">
    <div v-if="includeSenhaAtual" class="pwd-field">
      <label class="pwd-label" :for="ids.atual">{{ t('password.current') }}</label>
      <input
        :id="ids.atual"
        :value="senhaAtual"
        type="password"
        autocomplete="current-password"
        class="pwd-input"
        :disabled="disabled"
        :placeholder="t('password.currentPlaceholder')"
        @input="$emit('update:senhaAtual', $event.target.value)"
      />
    </div>

    <div class="pwd-field">
      <label class="pwd-label" :for="ids.nova">{{ t('password.new') }}</label>
      <input
        :id="ids.nova"
        :value="novaSenha"
        type="password"
        autocomplete="new-password"
        class="pwd-input"
        :disabled="disabled"
        :placeholder="placeholderNova || t('password.newPlaceholder')"
        @input="$emit('update:novaSenha', $event.target.value)"
      />
      <div v-if="(novaSenha || '').trim().length" class="pwd-meta">
        <div class="pwd-bar" role="progressbar" :aria-valuenow="strength.segments" aria-valuemin="0" aria-valuemax="3">
          <div
            v-for="n in 3"
            :key="n"
            class="pwd-bar__seg"
            :class="barSegClass(n)"
          />
        </div>
        <p class="pwd-strength-text" :class="'pwd-strength-text--' + (strength.level || 'empty')">
          {{ strengthLabel }}
        </p>
        <ul class="pwd-rules" :aria-label="t('password.rulesAria')">
          <li :class="ruleClass(rules.min6)">
            <span class="pwd-rules__icon">{{ rules.min6 ? '✓' : '○' }}</span>
            {{ t('password.ruleMin6') }}
          </li>
          <li :class="ruleClass(rules.hasLower)">
            <span class="pwd-rules__icon">{{ rules.hasLower ? '✓' : '○' }}</span>
            {{ t('password.ruleLower') }}
          </li>
          <li :class="ruleClass(rules.hasUpper)">
            <span class="pwd-rules__icon">{{ rules.hasUpper ? '✓' : '○' }}</span>
            {{ t('password.ruleUpper') }}
          </li>
          <li :class="ruleClass(rules.hasDigit)">
            <span class="pwd-rules__icon">{{ rules.hasDigit ? '✓' : '○' }}</span>
            {{ t('password.ruleDigit') }}
          </li>
          <li :class="ruleClass(rules.hasSpecial)">
            <span class="pwd-rules__icon">{{ rules.hasSpecial ? '✓' : '○' }}</span>
            {{ t('password.ruleSpecial') }}
          </li>
          <li :class="ruleClass(rules.length10)">
            <span class="pwd-rules__icon">{{ rules.length10 ? '✓' : '○' }}</span>
            {{ t('password.ruleLength10') }}
          </li>
        </ul>
      </div>
    </div>

    <div class="pwd-field">
      <label
        class="pwd-label"
        :class="{ 'pwd-label--error': showMismatch }"
        :for="ids.confirm"
      >
        {{ t('password.repeat') }}
      </label>
      <input
        :id="ids.confirm"
        :value="confirmarNovaSenha"
        type="password"
        autocomplete="new-password"
        class="pwd-input"
        :class="{
          'pwd-input--error': showMismatch,
          'pwd-input--ok': showMatchOk
        }"
        :disabled="disabled"
        :placeholder="t('password.repeatPlaceholder')"
        @input="$emit('update:confirmarNovaSenha', $event.target.value)"
      />
      <p v-if="showMismatch" class="pwd-feedback pwd-feedback--error">{{ t('password.mismatch') }}</p>
      <p v-else-if="showMatchOk" class="pwd-feedback pwd-feedback--ok">{{ t('password.matchOk') }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { evaluatePasswordStrength, passwordRulesStatus } from '../composables/usePasswordStrength'

const { t } = useI18n()

const props = defineProps({
  senhaAtual: { type: String, default: '' },
  /** exibe campo senha atual (ex.: modal de troca) */
  includeSenhaAtual: { type: Boolean, default: true },
  novaSenha: { type: String, default: '' },
  confirmarNovaSenha: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  /** prefixo único para ids (acessibilidade em vários modais) */
  idPrefix: { type: String, default: 'pwd' },
  placeholderNova: { type: String, default: '' }
})

defineEmits(['update:senhaAtual', 'update:novaSenha', 'update:confirmarNovaSenha'])

const ids = computed(() => ({
  atual: `${props.idPrefix}-atual`,
  nova: `${props.idPrefix}-nova`,
  confirm: `${props.idPrefix}-confirm`
}))

const strength = computed(() => evaluatePasswordStrength(props.novaSenha))
const strengthLabel = computed(() =>
  strength.value.labelKey ? t(strength.value.labelKey) : ''
)
const rules = computed(() => passwordRulesStatus(props.novaSenha))

const showMismatch = computed(
  () =>
    props.confirmarNovaSenha.length > 0 &&
    props.novaSenha !== props.confirmarNovaSenha
)

const showMatchOk = computed(
  () =>
    props.confirmarNovaSenha.length > 0 &&
    props.novaSenha === props.confirmarNovaSenha &&
    (props.novaSenha || '').trim().length >= 6
)

function ruleClass(ok) {
  return ok ? 'pwd-rules__li--ok' : 'pwd-rules__li--pending'
}

function barSegClass(n) {
  const { level, segments } = strength.value
  if (n > segments) return 'pwd-bar__seg--empty'
  if (level === 'weak' || level === 'empty') return 'pwd-bar__seg--weak'
  if (level === 'medium') return 'pwd-bar__seg--medium'
  return 'pwd-bar__seg--strong'
}
</script>

<style scoped>
.pwd-block {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.pwd-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.pwd-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 0.01em;
}

.pwd-label--error {
  color: var(--error);
}

.pwd-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
  color: var(--text-primary);
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-secondary);
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background-color 0.2s ease;
}

.pwd-input::placeholder {
  color: var(--text-muted);
}

.pwd-input:focus {
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent-primary) 28%, transparent);
}

.pwd-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pwd-input--error {
  border-color: var(--error) !important;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--error) 30%, transparent) !important;
}

.pwd-input--ok {
  border-color: var(--success) !important;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--success) 28%, transparent) !important;
}

.pwd-meta {
  margin-top: 0.5rem;
}

.pwd-bar {
  display: flex;
  gap: 6px;
  margin-bottom: 0.5rem;
}

.pwd-bar__seg {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  transition:
    background-color 0.35s ease,
    transform 0.25s ease;
}

.pwd-bar__seg--empty {
  background-color: var(--border-primary);
  opacity: 0.45;
}

.pwd-bar__seg--weak {
  background: linear-gradient(90deg, color-mix(in srgb, var(--error) 85%, #fff 0%), var(--error));
}

.pwd-bar__seg--medium {
  background: linear-gradient(90deg, color-mix(in srgb, var(--warning) 90%, #fff 0%), var(--warning));
}

.pwd-bar__seg--strong {
  background: linear-gradient(90deg, color-mix(in srgb, var(--success) 85%, #fff 0%), var(--success));
}

.pwd-strength-text {
  font-size: 0.8125rem;
  font-weight: 600;
  margin: 0 0 0.5rem;
}

.pwd-strength-text--empty {
  color: var(--text-muted);
}

.pwd-strength-text--weak {
  color: var(--error);
}

.pwd-strength-text--medium {
  color: var(--warning);
}

.pwd-strength-text--strong {
  color: var(--success);
}

.pwd-rules {
  list-style: none;
  margin: 0;
  padding: 0.65rem 0.75rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--bg-tertiary) 88%, transparent);
  border: 1px solid var(--border-primary);
  font-size: 0.75rem;
  line-height: 1.45;
}

.pwd-rules li {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--text-secondary);
  padding: 0.15rem 0;
}

.pwd-rules__icon {
  width: 1rem;
  display: inline-flex;
  justify-content: center;
  font-size: 0.65rem;
  opacity: 0.85;
}

.pwd-rules__li--pending {
  color: var(--text-tertiary);
}

.pwd-rules__li--ok {
  color: var(--success);
  font-weight: 500;
}

.pwd-feedback {
  font-size: 0.75rem;
  font-weight: 500;
  margin: 0.25rem 0 0;
}

.pwd-feedback--error {
  color: var(--error);
}

.pwd-feedback--ok {
  color: var(--success);
}
</style>
